package com.bulut.mecitogullari.navigation

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.List
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Icon
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.lifecycle.viewmodel.navigation3.rememberViewModelStoreNavEntryDecorator
import androidx.navigation3.runtime.NavKey
import androidx.navigation3.runtime.entryProvider
import androidx.navigation3.runtime.rememberNavBackStack
import androidx.navigation3.runtime.rememberSaveableStateHolderNavEntryDecorator
import androidx.navigation3.ui.NavDisplay
import androidx.savedstate.serialization.SavedStateConfiguration
import com.bulut.mecitogullari.core.platform.exitApp
import com.bulut.mecitogullari.core.platform.isExitConfirmationSupported
import com.bulut.mecitogullari.core.session.SessionManager
import com.bulut.mecitogullari.ui.screens.addtransaction.AddTransactionScreen
import com.bulut.mecitogullari.ui.screens.craftsmandetail.CraftsmanDetailScreen
import com.bulut.mecitogullari.ui.screens.craftsmen.CraftsmenScreen
import com.bulut.mecitogullari.ui.screens.dashboard.DashboardScreen
import com.bulut.mecitogullari.ui.screens.debts.DebtsScreen
import com.bulut.mecitogullari.ui.screens.login.LoginScreen
import com.bulut.mecitogullari.ui.screens.newproject.NewProjectScreen
import com.bulut.mecitogullari.ui.screens.profile.ProfileScreen
import com.bulut.mecitogullari.ui.screens.projectdetail.ProjectDetailScreen
import com.bulut.mecitogullari.ui.screens.projects.ProjectsScreen
import com.bulut.mecitogullari.ui.screens.purchase.PurchaseScreen
import com.bulut.mecitogullari.ui.screens.reports.ReportsScreen
import com.bulut.mecitogullari.ui.screens.supplierdetail.SupplierDetailScreen
import com.bulut.mecitogullari.ui.screens.transactions.TransactionsScreen
import com.bulut.mecitogullari.ui.theme.AppColors
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic
import kotlinx.serialization.modules.subclass

/**
 * Registers every route so the back stack can be saved/restored on all
 * platforms (NavKey is serialized polymorphically).
 */
private val navSavedStateConfiguration = SavedStateConfiguration {
    serializersModule = SerializersModule {
        polymorphic(NavKey::class) {
            subclass(LoginRoute::class)
            subclass(DashboardRoute::class)
            subclass(TransactionsRoute::class)
            subclass(ProjectsRoute::class)
            subclass(ReportsRoute::class)
            subclass(ProfileRoute::class)
            subclass(DebtsRoute::class)
            subclass(AddTransactionRoute::class)
            subclass(NewProjectRoute::class)
            subclass(ProjectDetailRoute::class)
            subclass(SupplierDetailRoute::class)
            subclass(PurchaseRoute::class)
            subclass(CraftsmenRoute::class)
            subclass(CraftsmanDetailRoute::class)
        }
    }
}

private data class BottomTab(
    val route: NavKey,
    val label: (com.bulut.mecitogullari.core.i18n.Strings) -> String,
    val icon: ImageVector,
)

private val bottomTabs = listOf(
    BottomTab(DashboardRoute, { it.tabHome }, Icons.Default.Home),
    BottomTab(TransactionsRoute, { it.tabTransactions }, Icons.Default.List),
    BottomTab(ProjectsRoute, { it.tabProjects }, Icons.Default.LocationOn),
    BottomTab(ReportsRoute, { it.tabReports }, Icons.Default.DateRange),
    BottomTab(ProfileRoute, { it.tabProfile }, Icons.Default.Person),
)

private const val TRANSITION_MS = 320
private const val TAB_TRANSITION_MS = 260

/**
 * Opaque wrapper for every destination so the outgoing screen can never
 * show through the incoming one during a transition.
 */
@Composable
private fun Screen(content: @Composable () -> Unit) {
    Surface(
        modifier = Modifier.fillMaxSize(),
        color = AppColors.Background,
        content = content,
    )
}

@Composable
fun AppNavigation() {
    val startRoute: NavKey = if (SessionManager.isLoggedIn) DashboardRoute else LoginRoute
    val backStack = rememberNavBackStack(navSavedStateConfiguration, startRoute)
    val currentRoute = backStack.lastOrNull()
    val showBottomBar = bottomTabs.any { it.route == currentRoute }

    // +1: target tab is to the right (content slides right→left),
    // -1: target tab is to the left (content slides left→right),
    // null: not a tab switch — use the regular push transition.
    var tabSwitchDirection by remember { mutableStateOf<Int?>(null) }

    var showExitDialog by remember { mutableStateOf(false) }

    fun switchTab(route: NavKey) {
        val current = backStack.lastOrNull()
        if (current == route) return
        val fromIndex = bottomTabs.indexOfFirst { it.route == current }
        val toIndex = bottomTabs.indexOfFirst { it.route == route }
        tabSwitchDirection = if (fromIndex in 0 until toIndex) 1 else -1
        backStack.clear()
        backStack.add(route)
    }

    fun push(route: NavKey) {
        tabSwitchDirection = null
        backStack.add(route)
    }

    fun resetTo(route: NavKey) {
        tabSwitchDirection = null
        backStack.clear()
        backStack.add(route)
    }

    // System back on a tab: non-Panel tabs return to Panel; on Panel,
    // ask before closing the app (Android only — iOS/web can't exit).
    @OptIn(ExperimentalComposeUiApi::class)
    BackHandler(enabled = showBottomBar) {
        if (currentRoute == DashboardRoute) {
            if (isExitConfirmationSupported) showExitDialog = true
        } else {
            switchTab(DashboardRoute)
        }
    }

    if (showExitDialog) {
        AlertDialog(
            onDismissRequest = { showExitDialog = false },
            title = { Text("Uygulamadan Çık") },
            text = { Text("Uygulamayı kapatmak istediğine emin misin?") },
            confirmButton = {
                TextButton(
                    onClick = {
                        showExitDialog = false
                        exitApp()
                    },
                ) {
                    Text("Çık", color = AppColors.Expense)
                }
            },
            dismissButton = {
                TextButton(onClick = { showExitDialog = false }) { Text("Vazgeç") }
            },
        )
    }

    Scaffold(
        containerColor = AppColors.Background,
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        bottomBar = {
            if (showBottomBar) {
                NavigationBar(containerColor = AppColors.Surface) {
                    val strings = com.bulut.mecitogullari.core.i18n.LocalStrings.current
                    bottomTabs.forEach { tab ->
                        NavigationBarItem(
                            selected = currentRoute == tab.route,
                            onClick = { switchTab(tab.route) },
                            icon = { Icon(tab.icon, contentDescription = tab.label(strings)) },
                            label = { Text(tab.label(strings)) },
                            colors = NavigationBarItemDefaults.colors(
                                selectedIconColor = AppColors.NavyDeep,
                                selectedTextColor = AppColors.NavyDeep,
                                indicatorColor = AppColors.Outline,
                                unselectedIconColor = AppColors.TextSecondary,
                                unselectedTextColor = AppColors.TextSecondary,
                            ),
                        )
                    }
                }
            }
        },
    ) { padding ->
        NavDisplay(
            backStack = backStack,
            modifier = Modifier.padding(padding),
            onBack = { backStack.removeLastOrNull() },
            entryDecorators = listOf(
                rememberSaveableStateHolderNavEntryDecorator(),
                rememberViewModelStoreNavEntryDecorator(),
            ),
            transitionSpec = {
                val tabDirection = tabSwitchDirection
                if (tabDirection != null) {
                    // Tab switch: direction follows the tab order, softened with a fade.
                    (slideInHorizontally(
                        animationSpec = tween(TAB_TRANSITION_MS, easing = FastOutSlowInEasing),
                    ) { fullWidth -> tabDirection * fullWidth / 4 } +
                        fadeIn(animationSpec = tween(TAB_TRANSITION_MS))) togetherWith
                        (slideOutHorizontally(
                            animationSpec = tween(TAB_TRANSITION_MS, easing = FastOutSlowInEasing),
                        ) { fullWidth -> -tabDirection * fullWidth / 4 } +
                            fadeOut(animationSpec = tween(TAB_TRANSITION_MS)))
                } else {
                    // Forward push: new screen slides in from the right, old one exits left.
                    slideInHorizontally(
                        animationSpec = tween(TRANSITION_MS, easing = FastOutSlowInEasing),
                    ) { fullWidth -> fullWidth } togetherWith
                        slideOutHorizontally(
                            animationSpec = tween(TRANSITION_MS, easing = FastOutSlowInEasing),
                        ) { fullWidth -> -fullWidth / 3 }
                }
            },
            // Back: previous screen returns from the left, current exits right.
            popTransitionSpec = {
                slideInHorizontally(
                    animationSpec = tween(TRANSITION_MS, easing = FastOutSlowInEasing),
                ) { fullWidth -> -fullWidth / 3 } togetherWith
                    slideOutHorizontally(
                        animationSpec = tween(TRANSITION_MS, easing = FastOutSlowInEasing),
                    ) { fullWidth -> fullWidth }
            },
            predictivePopTransitionSpec = { _ ->
                slideInHorizontally(
                    animationSpec = tween(TRANSITION_MS, easing = FastOutSlowInEasing),
                ) { fullWidth -> -fullWidth / 3 } togetherWith
                    slideOutHorizontally(
                        animationSpec = tween(TRANSITION_MS, easing = FastOutSlowInEasing),
                    ) { fullWidth -> fullWidth }
            },
            entryProvider = entryProvider {
                entry<LoginRoute> {
                    Screen {
                        LoginScreen(
                            onLoggedIn = { resetTo(DashboardRoute) },
                        )
                    }
                }

                entry<DashboardRoute> {
                    Screen {
                        DashboardScreen(
                            onAddTransaction = { push(AddTransactionRoute()) },
                            onTransactionClick = { id -> push(AddTransactionRoute(id)) },
                            onSeeAllTransactions = { switchTab(TransactionsRoute) },
                        )
                    }
                }

                entry<TransactionsRoute> {
                    Screen {
                        TransactionsScreen(
                            onTransactionClick = { id -> push(AddTransactionRoute(id)) },
                            onAddTransaction = { push(AddTransactionRoute()) },
                            onOpenDebts = { push(DebtsRoute) },
                            onOpenCraftsmen = { push(CraftsmenRoute) },
                        )
                    }
                }

                entry<ProjectsRoute> {
                    Screen {
                        ProjectsScreen(
                            onProjectClick = { id -> push(ProjectDetailRoute(id)) },
                            onNewProject = { push(NewProjectRoute) },
                        )
                    }
                }

                entry<DebtsRoute> {
                    Screen {
                        DebtsScreen(
                            onSupplierClick = { id -> push(SupplierDetailRoute(id)) },
                            onBack = { backStack.removeLastOrNull() },
                        )
                    }
                }

                entry<ReportsRoute> {
                    Screen {
                        ReportsScreen()
                    }
                }

                entry<ProfileRoute> {
                    Screen {
                        ProfileScreen(
                            onLoggedOut = { resetTo(LoginRoute) },
                            onOpenDebts = { push(DebtsRoute) },
                            onOpenCraftsmen = { push(CraftsmenRoute) },
                        )
                    }
                }

                entry<AddTransactionRoute> { route ->
                    Screen {
                        AddTransactionScreen(
                            transactionId = route.transactionId,
                            onClose = { backStack.removeLastOrNull() },
                        )
                    }
                }

                entry<NewProjectRoute> {
                    Screen {
                        NewProjectScreen(
                            onClose = { backStack.removeLastOrNull() },
                        )
                    }
                }

                entry<ProjectDetailRoute> { route ->
                    Screen {
                        ProjectDetailScreen(
                            projectId = route.projectId,
                            onTransactionClick = { id -> push(AddTransactionRoute(id)) },
                            onBack = { backStack.removeLastOrNull() },
                        )
                    }
                }

                entry<SupplierDetailRoute> { route ->
                    Screen {
                        SupplierDetailScreen(
                            supplierId = route.supplierId,
                            onAddPurchase = { push(PurchaseRoute(route.supplierId)) },
                            onEditPurchase = { entryId -> push(PurchaseRoute(route.supplierId, entryId)) },
                            onBack = { backStack.removeLastOrNull() },
                        )
                    }
                }

                entry<CraftsmenRoute> {
                    Screen {
                        CraftsmenScreen(
                            onCraftsmanClick = { id -> push(CraftsmanDetailRoute(id)) },
                            onBack = { backStack.removeLastOrNull() },
                        )
                    }
                }

                entry<CraftsmanDetailRoute> { route ->
                    Screen {
                        CraftsmanDetailScreen(
                            craftsmanId = route.craftsmanId,
                            onBack = { backStack.removeLastOrNull() },
                        )
                    }
                }

                entry<PurchaseRoute> { route ->
                    Screen {
                        PurchaseScreen(
                            supplierId = route.supplierId,
                            entryId = route.entryId,
                            onClose = { backStack.removeLastOrNull() },
                        )
                    }
                }
            },
        )
    }
}
